import type { CustomTextProps } from '~/modules/campaign-product-price/modules/product-variant-table/configs';
import type {
  BoxProps,
  CollapsibleProps,
  InlineStackProps,
  ThumbnailProps,
} from '@shopify/polaris';
import type { ComponentProps } from 'react';

// Straight at the file rather than the module root: that barrel reaches back into campaign-price
// through a long chain of other modules, and the cycle leaves GRID_SIZE unevaluated here.
import { GRID_SIZE } from '~/modules/campaign-product-price/modules/product-variant-table/configs';

/** The two amounts the Variant side asks a merchant to type, per product. */
export const PRICE_PRODUCT_FIELD = {
  PRICE: 'price',
  COMPARE_PRICE: 'comparePrice',
} as const;

/**
 * What a price field accepts while it is being typed: digits, at most one decimal point, and the
 * empty string. Deliberately lenient about a trailing dot — `12.` is a legal thing to be halfway
 * through typing, and rejecting it would swallow the keystroke.
 *
 * One more digit is refused once the value reads `0.00` (or `.00`): `0.00` itself is accepted, but
 * `0.000`–`0.009` and anything longer are not. Save sends two decimals, so such a value would only go
 * out as `0` (or `0.01`), and no currency prices a product that low. A non-zero whole part
 * (`1.001`) or a non-zero first two decimals (`0.01`) are left as they were.
 */
export const PRICE_PRODUCT_INPUT_PATTERN = /^(?!0*\.00\d)\d*\.?\d*$/;

/** Both amounts in one list, for anything that has to walk a whole draft rather than one field. */
export const PRICE_PRODUCT_FIELDS = Object.values(PRICE_PRODUCT_FIELD);

/** Where each field is held on the challenger's `productVariants` entry for that variant. */
export const PRICE_PRODUCT_PRODUCT_VARIANT_KEY = {
  [PRICE_PRODUCT_FIELD.PRICE]: 'price',
  [PRICE_PRODUCT_FIELD.COMPARE_PRICE]: 'compareAtPrice',
} as const;

// 38px per the design, not the 50px the older table spends: a Polaris Checkbox measures 22px across
// once its 1px backdrop margins are counted, so this is the control with `space-200` either side.
export const PRICE_PRODUCT_CHECKBOX_COLUMN = '38px';

/**
 * 180px per the design with the Setup guide panel closed, putting a Control/Variant group at the
 * 360px it is drawn to; with the panel open there is less room, so the design narrows each column to
 * 150px instead (a 300px group). Hard-fixed rather than shared with the product column's slack: a
 * group has to measure the same whatever the card is wide, since the head above it merges the pair
 * into one cell.
 */
export const PRICE_PRODUCT_PRICE_COLUMN_WIDTH = { DEFAULT: 180, COMPACT: 150 } as const;

/**
 * 212px per the design — the checkbox, thumbnail and a sliver of room beyond them that the merged
 * column never shrinks past, whether it is filling slack space or collapsed to its own content.
 */
export const PRICE_PRODUCT_MERGED_COLUMN_MIN_WIDTH = '212px';

// The product column spends whatever is left of the merged column's floor once the checkbox and
// thumbnail have taken their share, so the two stay in step without that floor being written out
// twice.
const PRODUCT_COLUMN_MIN_WIDTH = `calc(${PRICE_PRODUCT_MERGED_COLUMN_MIN_WIDTH} - ${PRICE_PRODUCT_CHECKBOX_COLUMN} - ${GRID_SIZE['THUMBNAIL']})`;

// The product column takes the slack so the table always fills its card, down to its floor. Both
// grids below spend the same fixed width on the price columns, so their single `1fr` resolves to the
// same leftover and the group boundaries land exactly on the Price column edges — an `auto` track
// would not, since the two grids start from different fixed totals.
export const PRICE_PRODUCT_PRODUCT_COLUMN_TRACK = `minmax(${PRODUCT_COLUMN_MIN_WIDTH}, 1fr)`;
export const PRICE_PRODUCT_HEAD_GROUP_COLUMN_TRACK = `minmax(${PRICE_PRODUCT_MERGED_COLUMN_MIN_WIDTH}, 1fr)`;

/**
 * With the Setup guide panel open, the title has to stay gone whatever room is left over, not just
 * shrunk — a `1fr` track still claims any slack the card has beyond the other columns' fixed widths,
 * which is what kept leaving it just enough space for one stray letter. Fixed rather than flexible is
 * also what the design itself draws this state as: the merged column sits at its floor ("Hug")
 * instead of filling ("Fill"). The older table's `GRID_SIZE['PRODUCT_COLUMN']` (248px) is not reused
 * here either way — that floor is sized for a variant-count link this column doesn't carry.
 */
export const PRICE_PRODUCT_PRODUCT_COLUMN_TRACK_COMPACT = PRODUCT_COLUMN_MIN_WIDTH;
export const PRICE_PRODUCT_HEAD_GROUP_COLUMN_TRACK_COMPACT = PRICE_PRODUCT_MERGED_COLUMN_MIN_WIDTH;

/**
 * The rows take whatever the screen has left rather than a fixed count of them, so the table runs to
 * the bottom of the window instead of scrolling a short box inside a half-empty page. The 240px is
 * what the page spends around the rows — roughly 175px above for the title bar, the advance-setting
 * card and the gaps between them, the 55px footer below, and the rest clearing the window's edge by
 * about a row's own padding. The floor keeps a tablet-height window showing several rows rather than
 * collapsing the table to one or two; below it the page scrolls, and the footer stays reachable by
 * sticking to the window.
 *
 * A floor under the cap rather than a `min-height`: the table is still only as tall as the rows it
 * holds, so a single product does not open a 320px box with 270px of nothing under it.
 */
export const PRICE_PRODUCT_TABLE_SCROLL_HEIGHT = 'max-h-[max(320px,calc(100vh-240px))]';

// Well clear of the 20 Polaris gives a TextField's contents — its root sets no stacking context of
// its own, so at anything lower the price fields scroll over the pinned rows instead of under them.
const TABLE_PINNED_Z_INDEX = '90';

/**
 * The head rows scroll with the body rather than sitting above it, and stick to the top instead —
 * a scroll container of their own would keep its own width, and the body's scrollbar would then eat
 * a dozen pixels off the body's grid alone and walk every column out of line with its heading.
 */
export const PRICE_PRODUCT_TABLE_HEAD_STICKY: BoxProps = {
  position: 'sticky',
  insetBlockStart: '0',
  zIndex: TABLE_PINNED_Z_INDEX,
  background: 'bg-surface',
};

/**
 * A grid item sizes itself to its content unless told otherwise, which is what lets a long product
 * title push past its column rather than truncate inside it. Applied to every cell, and again to
 * whatever the cell puts in its flex row, since a flex item starts from the same default.
 */
export const PRICE_PRODUCT_CELL_MIN_WIDTH = '0';

/**
 * A ticked row is filled, which is what makes a selection readable once it spans rows that have
 * scrolled apart — a product carries it as soon as any of its variants is ticked, so an open product
 * still shows its own state without every variant having to be in view.
 */
export const PRICE_PRODUCT_ROW_SELECTED_BACKGROUND: BoxProps['background'] = 'bg-surface-secondary';
export const PRICE_PRODUCT_TRUNCATE_CLASS = 'min-w-0 grow overflow-hidden';

/**
 * A product row names itself as a hover group, so the actions parked in one of its cells answer the
 * pointer anywhere on the row rather than only over that cell.
 */
export const PRICE_PRODUCT_ROW_GROUP_CLASS = 'group/price-product-row';

/**
 * Faded rather than unmounted: the button keeps its width either way, so the title beside it truncates
 * to the same point whether the pointer is on the row or not. Shown on focus too, since it is the only
 * way to reach it without a pointer.
 */
export const PRICE_PRODUCT_ROW_ACTION_CLASS =
  'shrink-0 opacity-0 transition-opacity focus-within:opacity-100 group-hover/price-product-row:opacity-100';

/** Fixed height of the empty state per the design, instead of letting padding drive it. */
export const PRICE_PRODUCT_TABLE_EMPTY_HEIGHT = '96px';

/** Separates a group's name from the note after it, e.g. `Control · Current price`. */
export const PRICE_PRODUCT_LABEL_SEPARATOR = '·';

/** Joins the two ends of an amount range shown inside one field, e.g. `275 - 2750`. */
export const PRICE_PRODUCT_RANGE_SEPARATOR = ' - ';

/** How a quick-fill rule moves the Control amount it starts from. */
export const PRICE_PRODUCT_QUICK_FILL_OPERATOR = {
  INCREASE: '+',
  DECREASE: '-',
  SET: '=',
} as const;

/**
 * What a quick-fill amount means. CURRENCY rather than a `$` literal: the symbol is the shop's and is
 * only known at render, so the stored rule names the unit and the select labels it.
 */
export const PRICE_PRODUCT_QUICK_FILL_UNIT = {
  PERCENT: 'percent',
  CURRENCY: 'currency',
} as const;

/** The one button that opens the rule bar and then commits it — see `toQuickFillLabel`. */
export const PRICE_PRODUCT_QUICK_FILL_LABEL = {
  IDLE: 'Quick fill',
  SELECTED: 'Quick fill selected',
  APPLY: 'Apply',
} as const;

/**
 * The footer button reads `Add product` while more products can still be added, and `Change product`
 * once the table is capped at the one it already holds — it stays on screen either way rather than
 * disappearing when there is nothing left to add.
 */
export const PRICE_PRODUCT_FOOTER_BUTTON_LABEL = {
  ADD: 'Add product',
  CHANGE: 'Change product',
} as const;

export const PRICE_PRODUCT_PERCENT_SYMBOL = '%';
export const PRICE_PRODUCT_PERCENT_BASE = 100;

/**
 * Three controls have to fit the width of one price column, so the operator and the unit hug their
 * own one-character label and the amount takes whatever is left. 36px per the design, which is what
 * that character measures with the select's own inset either side — a minimum rather than a width,
 * since a shop whose currency symbol runs to several characters has to spend more than one.
 */
const QUICK_FILL_SELECT_MIN_WIDTH = '36px';

/**
 * Found by PriceProductTable.scss, which strips the select back to that bare label — the design
 * draws `+` and `%` alone, where Polaris gives a Select a caret and the room to put one.
 */
const QUICK_FILL_SELECT_CLASS = 'price-product-quick-fill-select';

/** What each of the rule bar's two selects is wrapped in. */
export const PRICE_PRODUCT_QUICK_FILL_SELECT_WRAPPER: ComponentProps<'div'> = {
  className: QUICK_FILL_SELECT_CLASS,
  style: { minWidth: QUICK_FILL_SELECT_MIN_WIDTH },
};

/** The gap between the three controls of a rule, per the design. */
export const PRICE_PRODUCT_QUICK_FILL_CONTROL_GAP: InlineStackProps['gap'] = '100';

/**
 * The product-row price fields say what they would overwrite, so the tooltip has to be up before the
 * merchant commits to the field rather than after Polaris' usual half-second.
 */
export const PRICE_PRODUCT_TOOLTIP_DELAY = 0;

/**
 * Drawn on a variant's own field while the product row's matching field has focus, so the merchant
 * can see every field that field's edit is about to overwrite — see PriceProductTable.scss.
 */
export const PRICE_PRODUCT_BROADCAST_TARGET_CLASS = 'price-product-broadcast-target';

/**
 * Row heights the design fixes. Set on the row itself rather than left to cell padding: a Polaris
 * Checkbox and a TextField are each taller than the text beside them, so padding-driven rows come
 * out at whatever the tallest control happens to measure.
 */
export const PRICE_PRODUCT_ROW_HEIGHT = {
  HEAD_GROUP: '52px',
  COLUMN_HEAD: '36px',
  /** A product standing for a single variant — its title carries no variant-count line. */
  PRODUCT: '52px',
  /** A product standing for several, so its title is two lines deep. */
  PRODUCT_WITH_VARIANTS: '60px',
  VARIANT: '52px',
  QUICK_FILL: '52px',
} as const;

/** The caret beside the variant count carries the tone of the subdued line it sits on. */

/**
 * The variant count and `Collapse all`, which the design draws as plain lines of text that answer the
 * pointer and nothing more — no fill in any state, only the cursor. Plain elements rather than
 * buttons, so nothing of a control's own chrome has to be stripped back off them.
 */
export const PRICE_PRODUCT_TEXT_ACTION_CLASS = 'flex w-fit cursor-pointer select-none items-center';

/**
 * `Collapse all` as it sits inside the `Products · Collapse all` heading — plain inline text rather
 * than the flex wrapper above, since a flex item is atomic to line-break truncation and would vanish
 * whole instead of fading into the heading's own ellipsis once the Setup guide panel narrows the
 * column.
 */
export const PRICE_PRODUCT_HEAD_ACTION_CLASS = 'cursor-pointer select-none';

/**
 * Wraps `Products · Collapse all` as the one line the design narrows down to a single ellipsis
 * instead of either chunk wrapping or disappearing on its own — see `PRICE_PRODUCT_HEAD_ACTION_CLASS`
 * for why the clickable half of it has to stay plain inline text for this to clip mid-word.
 */
export const PRICE_PRODUCT_HEAD_LABEL_TRUNCATE_CLASS = 'min-w-0 grow truncate';

/**
 * The variant-count line is subdued until the pointer is on it, when it comes up to the default text
 * colour. Left as literals so the same pair types against both Polaris' Icon and its Text, whose tone
 * unions only overlap on the values used here.
 */
export const PRICE_PRODUCT_VARIANT_COUNT_TONE = {
  DEFAULT: 'subdued',
  HOVERED: 'base',
} as const;

/**
 * Animates a product row's own height, so landing a detail fetch that turns out to have several
 * variants grows the row into its taller height instead of snapping it — the same jump the variant
 * list opening beneath it would otherwise make on its own.
 */
export const PRICE_PRODUCT_ROW_TRANSITION = 'height 150ms ease-in-out';

/** How the variant list slides open under a product row once it is known to have more than one. */
export const PRICE_PRODUCT_EXPAND_TRANSITION: CollapsibleProps['transition'] = {
  duration: '150ms',
  timingFunction: 'ease-in-out',
};

/** Fixed to the height its real content takes, so a field's skeleton never itself moves the row. */
export const PRICE_PRODUCT_SKELETON_HEIGHT: Record<'TEXT' | 'FIELD', string> = {
  TEXT: '20px',
  FIELD: '36px',
};

/** 40px per the design — Polaris' `small`, which is also what the 52px row is sized to hold. */
export const PRICE_PRODUCT_THUMBNAIL_SIZE: ThumbnailProps['size'] = 'small';

/**
 * Cells only pad horizontally — the row owns its height and centres them, so vertical padding would
 * only push content out of a row it cannot grow. The price columns are inset from the group boundary
 * they sit against and tighten towards each other, which is what pairs them visually.
 */
export const PRICE_PRODUCT_CELL_PADDING: Record<
  'PRODUCT' | 'GROUP' | 'PRICE' | 'COMPARE_PRICE',
  BoxProps
> = {
  PRODUCT: {
    paddingInlineEnd: '300',
  },
  GROUP: {
    paddingInline: '400',
  },
  PRICE: {
    paddingInlineStart: '400',
    paddingInlineEnd: '200',
  },
  COMPARE_PRICE: {
    paddingInlineStart: '200',
    paddingInlineEnd: '400',
  },
};

/**
 * The line closing a row. Solid between one product and the next; dashed inside an open product, so
 * its variants read as one block hanging off it rather than as rows of the same rank.
 */
export const PRICE_PRODUCT_ROW_BORDER_STYLE: Record<'SOLID' | 'DASHED', BoxProps['borderStyle']> = {
  SOLID: 'solid',
  DASHED: 'dashed',
};

/** Drawn on the cell that opens a group, so the Control and Variant sides read as separate columns. */
export const PRICE_PRODUCT_GROUP_DIVIDER: BoxProps = {
  borderColor: 'border',
  borderInlineStartWidth: '025',
};

// The footer is a row of its own rather than a strip under the table, so it is padded to a row's
// height instead of a cell's — the add-product button then reads as centred in it.
export const PRICE_PRODUCT_FOOTER_PADDING: BoxProps = {
  paddingInline: '400',
  paddingBlock: '400',
};

/**
 * The footer sits outside the table's sideways scroller and pins to the bottom of the window, so the
 * one way to add a product stays in view however long the list runs and however far the grid is
 * scrolled across. It rides up over the rows rather than out of the card — `position: sticky` moves
 * an element inside its own box, and the card only clips what would leave that box.
 */
export const PRICE_PRODUCT_FOOTER_STICKY: BoxProps = {
  position: 'sticky',
  insetBlockEnd: '0',
  zIndex: TABLE_PINNED_Z_INDEX,
  background: 'bg-surface',
};

/**
 * The group row reads as `heading-md` — 14px/20px at weight 650 — the same as the shared HEAD_GROUP
 * style. The note beside a group's name keeps that weight and only drops in tone, which is what
 * makes `Control` the label and `Current price` the aside.
 */
export const PRICE_PRODUCT_TEXT_PROPS: Record<'GROUP_NOTE', CustomTextProps> = {
  GROUP_NOTE: {
    as: 'span',
    variant: 'headingMd',
    fontWeight: 'regular',
    tone: 'subdued',
  },
};
